import React from "react";
import { useSelector, useDispatch } from "react-redux";
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import {
    Button,
    Typography,
    Card,
    Checkbox,
    Input,
} from "@material-tailwind/react";

import { DialogAddEmployee } from "./DialogAddEmployee";
import { Search, Refresh, User } from "iconoir-react";
import { getMoneyFormat } from "../../../utils/formatViews";
import {
    Employee,
    listEmployeesRequest,
    listEmployeesSuccess,
    setEmployeeRequest,
    updateEmployeesRequest,
    createEmployeeRequest,
} from "../../../redux/employee/actions";
import AppImages from "../../../assets/AppImages";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../redux";
import Loader from "../../../components/Loader";


const TABLE_HEAD = [
    { n: "Name", className: "w-48" },
    { n: "Email", className: "" },
    { n: "Hour Rate", className: "" },
    { n: "Weekly Cap", className: "" },
    { n: "Payouts", className: "w-28" },
];

const DashboardEmployees = () => {
    const { employees, loading } = useSelector((state: RootState) => state.employees);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = React.useState(false);
    const [search, setSearch] = React.useState<string>("");
    const [filterEmployees, setFilterEmployees] = React.useState<Employee[]>([]);
    const [selectedEmployees, setSelectedEmployees] = React.useState<number[]>([]);

    const handleRefresh = () => {
        dispatch(
            listEmployeesRequest({
                filters: "",
                members_fields: [
                    "id",
                    "user",
                    "avatar",
                    "hour_rate",
                    "weekly_cap",
                    "payouts",
                ].join(","),
                excludes: "",
            })
        );
    };

    React.useEffect(() => {
        const handleFilterApply = () => {
            let result = [...employees];

            if (search) {
                result = result.filter((el) => {
                    const searchString = `${el?.user?.first_name} ${el?.user?.last_name} ${el?.user?.email}`;
                    return searchString.toLowerCase().includes(search.toLowerCase());
                });
            }

            setFilterEmployees(result);
        };

        handleFilterApply();
    }, [employees, search]);

    const handleCheckboxChange = (id: number) => {
        setSelectedEmployees((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    return (
        <div className="flex flex-col gap-y-5 mt-5 text-black w-full">
            <Card>
                <Card.Header className="w-full m-0 items-start rounded-none flex flex-wrap gap-4 justify-between p-4">
                    <div>
                        <Typography type="lead" color="default" className="font-semibold mb-1">
                            Employees
                        </Typography>
                        <Typography type="small" className="text-foreground block">
                            Here you have details about the company
                        </Typography>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center w-full shrink-0 gap-3 md:w-max">
                        <Input
                            placeholder="Search here..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full sm:w-72"
                        >
                            <Input.Icon>
                                <Search className="w-full h-full" />
                            </Input.Icon>
                        </Input>
                        <div className="flex gap-3 sm:w-max w-full">
                            <Button
                                variant="outline"
                                color="secondary"
                                className="flex items-center gap-2 sm:w-auto w-full"
                                onClick={() => {
                                    handleRefresh();
                                }}
                            >
                                <Refresh className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="outline"
                                color="error"
                                className="flex items-center gap-2 sm:w-auto w-full"
                                onClick={() => {
                                    if (selectedEmployees.length == 0) {
                                        return;
                                    }

                                    withReactContent(Swal)
                                        .fire({
                                            title: "Are you sure?",
                                            text: `You are about to remove ${selectedEmployees.length} employees`,
                                            icon: "warning",
                                            showCancelButton: true,
                                            confirmButtonColor: "#EF4444",
                                            confirmButtonText: "Remove",
                                        })
                                        .then((result: any) => {
                                            if (result.isConfirmed) {
                                                dispatch(
                                                    updateEmployeesRequest({
                                                        employees: selectedEmployees.map((id) => ({
                                                            id: id,
                                                            is_removed: true,
                                                        })),
                                                        update_fields: ["id", "is_removed"],
                                                    })
                                                );
                                                dispatch(
                                                    listEmployeesSuccess(
                                                        employees.filter(
                                                            (t) =>
                                                                !selectedEmployees.includes(
                                                                    t.id || 0
                                                                )
                                                        )
                                                    )
                                                );
                                                setSelectedEmployees([]);
                                            }
                                        })
                                        .catch((error: any) => {
                                            console.log("error", error);
                                        });
                                }}
                            >
                                Remove
                                <AppImages.iconTrash className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="outline"
                                color="primary"
                                className="flex items-center gap-2 sm:w-auto w-full text-nowrap"
                                onClick={() => setIsOpen(true)}
                            >
                                Add Employee
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="mt-4 p-0 rounded-none max-h-[calc(100vh-240px)] overflow-y-auto scrollbar-hide">
                    <table className="w-full min-w-max table-auto">
                        <thead>
                            <tr>
                                {TABLE_HEAD.map((el, idx: number) => (
                                    <th
                                        key={idx}
                                        className={`sticky top-0 bg-white z-10 border-b text-start border-surface p-4 ${el.className}`}
                                    >
                                        {idx == 0 ? (
                                            <div className="flex items-center gap-5">
                                                <Checkbox
                                                    id="checkbox_all"
                                                    key={`${Math.random()}`}
                                                    checked={
                                                        selectedEmployees.length ==
                                                        filterEmployees.length
                                                    }
                                                    onChange={() => {
                                                        if (
                                                            selectedEmployees.length ==
                                                            filterEmployees.length
                                                        ) {
                                                            setSelectedEmployees([]);
                                                        } else {
                                                            setSelectedEmployees(
                                                                filterEmployees.map(
                                                                    (el) => el.id || 0
                                                                )
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <Checkbox.Indicator />
                                                </Checkbox>
                                                <Typography
                                                    type="small"
                                                    color="default"
                                                    className="block font-medium"
                                                >
                                                    {el.n}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <Typography
                                                type="small"
                                                color="default"
                                                className="block font-medium"
                                            >
                                                {el.n}
                                            </Typography>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filterEmployees?.map((el: Employee) => (
                                <tr
                                    key={el.id}
                                    onClick={(e) => {
                                        if (
                                            (e.target as HTMLElement).tagName === "INPUT" ||
                                            (e.target as HTMLElement).tagName === "IMG" ||
                                            (e.target as HTMLElement).tagName === "LABEL"
                                        ) {
                                            return;
                                        }
                                        return;

                                        dispatch(setEmployeeRequest(el));
                                        navigate("/dashboard/employees/" + el.id);
                                    }}
                                >
                                    <td className="px-4 border-b border-surface">
                                        <div className="flex items-center gap-5">
                                            <Checkbox
                                                key={`${el.id}_${Math.random()}`}
                                                id={`checkbox_${el?.id || 0}`}
                                                checked={selectedEmployees.includes(el?.id || 0)}
                                                onChange={() => handleCheckboxChange(el?.id || 0)}
                                            >
                                                <Checkbox.Indicator />
                                            </Checkbox>
                                            {el.avatar ? (
                                                <div className="flex items-center justify-center w-7 h-7 bg-gray-200 rounded-full">
                                                    <img
                                                        src={el.avatar}
                                                        alt="avatar"
                                                        className="h-5 w-5 rounded-full"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center w-7 h-7 bg-gray-200 rounded-full">
                                                    <User className="h-5 w-5" />
                                                </div>
                                            )}
                                            <Typography
                                                type="small"
                                                color="default"
                                                className="py-4 overflow-hidden flex items-center text-ellipsis w-48 !text-nowrap text-foreground font-medium"
                                            >
                                                {el.user?.first_name} {el.user?.last_name}
                                            </Typography>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {el.user?.email}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {getMoneyFormat(el?.hour_rate)}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {getMoneyFormat(el?.weekly_cap)}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {getMoneyFormat(el?.payouts)}
                                        </Typography>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card.Body>
            </Card>

            {loading && (
                <div className="flex justify-center items-center h-full">
                    <Loader />
                </div>
            )}

            {isOpen && (
                <DialogAddEmployee
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    onSubmit={(employee: Employee) => {
                        setIsOpen(false);
                        dispatch(
                            createEmployeeRequest({
                                employee: employee,
                                update_fields: Object.keys(employee),
                            })
                        );
                    }}
                />
            )}
        </div>
    );
};

export default DashboardEmployees;
